import { queryOptions, type QueryClient } from '@tanstack/react-query';
import { qontoClient } from 'receivable-invoices-js/react/utils/api-clients/qonto';
import { camelizeKeys } from 'receivable-invoices-js/react/utils/camelize-keys';
import type { PaymentLinkAPI } from 'receivable-invoices-js/react/api/models/payment-link';
import { paymentLinkNamespace } from 'receivable-invoices-js/constants/hosts';

interface FindItemParams {
  resourceId: string;
}

interface PaymentLinkResponse {
  data: {
    id: string;
    type: string;
    attributes: PaymentLinkAPI;
  };
}

export const paymentLinkQueries = {
  all: ['payment-links'] as const,
  items: () => [...paymentLinkQueries.all, 'item'] as const,
  findItem: (params: FindItemParams) =>
    queryOptions({
      queryKey: [...paymentLinkQueries.items(), params],
      async queryFn(ctx) {
        // Present but missing in type
        // https://tanstack.com/query/latest/docs/framework/react/guides/query-functions#queryfunctioncontext
        const { client } = ctx as unknown as { client: QueryClient };

        const res = await qontoClient<PaymentLinkResponse>(
          `${paymentLinkNamespace}/payment_links/checkout`,
          {
            params: { resource_id: params.resourceId },
          }
        );

        const paymentLink = camelizeKeys({
          ...res.data.attributes,
          id: res.data.id,
        });

        // Set the item in cache by ID
        client.setQueryData(paymentLinkQueries.item(res.data.id).queryKey, paymentLink);

        return paymentLink;
      },
    }),
  item: (id: string) =>
    queryOptions({
      queryKey: [...paymentLinkQueries.items(), id],
      async queryFn() {
        const res = await qontoClient<PaymentLinkResponse>(
          `${paymentLinkNamespace}/payment_links/checkout/${id}`
        );

        return camelizeKeys({
          ...res.data.attributes,
          id: res.data.id,
        });
      },
    }),
};
